import {
  AmbassadorRecord,
  CreateAmbassadorRequest,
  RepresentationTypes,
  UpdateAmbassadorRequest,
} from '@amzn/red-velvet-api';
// Type-only: the form module imports HEARD_ABOUT_VALUES back from here, and a value import in both
// directions would be a runtime cycle. `import type` is erased, so only this direction survives.
import type { AmbassadorRegistrationFormValues } from '../components/ambassadors/ambassadorRegistrationForm';

/**
 * The values persisted as `heardAbout`, keyed to the option each one belongs to. Locale-independent
 * by design: these are the English strings the form has always sent, so records written before and
 * after localization stay comparable. The option's translated label is for display only and must
 * never reach the request — otherwise a German registrant's acquisition source is stored in German
 * and the reporting on it fragments by UI language.
 */
export const HEARD_ABOUT_VALUES = {
  aciWiki: 'ACI Wiki',
  togetherAtAmazon: 'Together at Amazon',
  existingCommunityAmbassadorReferral: 'Existing Community Ambassador Referral',
  internalCommunications: 'Internal Communications',
  aToZ: 'AtoZ',
  leadershipRecommendation: 'Leadership recommendation',
  other: 'Other - please specify',
} as const;

export const ambassadorFormValuesToRequest = (
  values: AmbassadorRegistrationFormValues,
  alias = ''
): CreateAmbassadorRequest => {
  const representationTypes: RepresentationTypes[] = [];
  if (values.siteRepresentation) {
    representationTypes.push(RepresentationTypes.SITE);
  }
  if (values.teamRepresentation) {
    representationTypes.push(RepresentationTypes.TEAM);
  }
  if (values.employeeLedGroupRepresentation) {
    representationTypes.push(RepresentationTypes.ELG);
  }

  return {
    alias,
    representationTypes: representationTypes,
    sendCopyOfResponses: values.sendMeACopy,
    heardAbout:
      values.howDidYouHear?.value === HEARD_ABOUT_VALUES.other
        ? [values.howDidYouHearSpecification]
        : [values.howDidYouHear?.value || ''],
    representedSiteCodes: values.representedSiteCodes.length
      ? values.representedSiteCodes.map((sc) => sc.value || '')
      : undefined,
    teamLocation: values.teamLocation?.length
      ? values.teamLocation?.map((tl) => tl.value || '')
      : undefined,
    teamName: values.teamName ?? undefined,
    ergName: values.ergName?.value ? [values.ergName.value] : undefined,
    ergLocation: values.ergLocation?.length
      ? values.ergLocation?.map((ergL) => ergL.value || '')
      : undefined,
  };
};

export const buildAmbassadorDirectoryOptions = (
  ambassadors: AmbassadorRecord[],
  propertyToAggregate: string
) => {
  const unique = new Set();

  if (ambassadors.length) {
    for (let i = 0; i < ambassadors.length; i++) {
      const obj = ambassadors?.[i];
      // Ensure object is non-null and has its own siteCode property
      if (obj && Object.prototype.hasOwnProperty.call(obj, propertyToAggregate)) {
        const code = obj[propertyToAggregate as keyof typeof obj];
        // Skip undefined/null; convert to string for the final string array
        if (code != null) {
          unique.add(String(code));
        }
      }
    }
  }

  // Convert Set to array (in insertion order)
  return Array.from(unique)
    .sort()
    .map((u) => ({ value: u as string, label: u as string }));
};

// this is different from above because we send employeeId instead of alias and dont send heardAbout
export const ambassadorFormValuesToUpdateRequest = (
  values: AmbassadorRegistrationFormValues,
  employeeId = ''
): UpdateAmbassadorRequest => {
  const representationTypes: RepresentationTypes[] = [];
  if (values.siteRepresentation) {
    representationTypes.push(RepresentationTypes.SITE);
  }
  if (values.teamRepresentation) {
    representationTypes.push(RepresentationTypes.TEAM);
  }
  if (values.employeeLedGroupRepresentation) {
    representationTypes.push(RepresentationTypes.ELG);
  }

  return {
    employeeId,
    representationTypes: representationTypes,
    representedSiteCodes: values.representedSiteCodes.length
      ? values.representedSiteCodes.map((sc) => sc.value || '')
      : undefined,
    teamLocation: values.teamLocation?.length
      ? values.teamLocation?.map((tl) => tl.value || '')
      : undefined,
    teamName: values.teamName ?? undefined,
    ergName: values.ergName?.value ? [values.ergName.value] : undefined,
    ergLocation: values.ergLocation?.length
      ? values.ergLocation?.map((ergL) => ergL.value || '')
      : undefined,
  };
};
